import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message } from 'antd';
import { LockOutlined, MailOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { adminAuthAPI, getAdminToken, storeAdminToken } from '../services/api';
import { useSiteBranding } from '../hooks/useSiteBranding';

const { Title, Text } = Typography;

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const { siteName, adminLoginLogoUrl } = useSiteBranding();

  useEffect(() => {
    const token = getAdminToken();
    if (token) {
      adminAuthAPI.me()
        .then(() => navigate('/admin', { replace: true }))
        .catch(() => {});
    }
  }, [navigate]);

  useEffect(() => {
    setLogoFailed(false);
  }, [adminLoginLogoUrl]);

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const res = await adminAuthAPI.login(values);
      storeAdminToken(res.access_token);
      message.success('管理员登录成功');
      navigate('/admin', { replace: true });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((e: any) => e?.msg || String(e)).join('; ') : (detail?.msg || '登录失败'));
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0B2545 0%, #13315C 50%, #1B4175 100%)',
    }}>
      <Card style={{
        width: 420,
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        borderRadius: 12,
        borderTop: '3px solid #C5A55A',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {adminLoginLogoUrl && !logoFailed ? (
            <img
              src={adminLoginLogoUrl}
              alt={siteName}
              style={{ maxHeight: 52, maxWidth: 220, objectFit: 'contain', marginBottom: 12 }}
              onError={() => setLogoFailed(true)}
            />
          ) : null}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 4 }}>
            <SafetyCertificateOutlined style={{ fontSize: 20, color: '#C5A55A' }} />
            <Title level={4} style={{ margin: 0, color: '#0B2545' }}>系统管理后台</Title>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>{siteName} Admin Console</Text>
        </div>
        <Form onFinish={onFinish} size="large">
          <Form.Item name="email" rules={[
            { required: true, message: '请输入管理员账号' },
          ]}>
            <Input prefix={<MailOutlined />} placeholder="管理员账号（邮箱或用户名）" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="密码" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block
              style={{ height: 44, fontWeight: 600 }}>
              管理员登录
            </Button>
          </Form.Item>
        </Form>
        <div style={{ textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            仅限系统管理员使用 · 与用户登录独立
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default AdminLogin;
